import React, { useMemo, useState } from 'react';
import { LandingPage } from './pages/LandingPage';
import { LandingPageV1 } from './pages/LandingPageV1';
import { LandingPageLight } from './pages/LandingPageLight';
import { LandingPageV1Light } from './pages/LandingPageV1Light';
import { BoltInspired } from './pages/BoltInspired';
import { BoltInspiredBase } from './pages/BoltInspiredBase';
import { BoltInspiredLight } from './pages/BoltInspiredLight';
import { BoltInspiredLightBase } from './pages/BoltInspiredLightBase';
import { OfficeHQInspired } from './pages/OfficeHQInspired';
import { OfficeHQInspiredBase } from './pages/OfficeHQInspiredBase';
import { OfficeHQInspiredLight } from './pages/OfficeHQInspiredLight';
import { OfficeHQInspiredLightBase } from './pages/OfficeHQInspiredLightBase';
import { PhoneServicesInspired } from './pages/PhoneServicesInspired';
import { PhoneServicesInspiredBase } from './pages/PhoneServicesInspiredBase';
import { PhoneServicesInspiredLight } from './pages/PhoneServicesInspiredLight';
import { PhoneServicesInspiredLightBase } from './pages/PhoneServicesInspiredLightBase';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
type Concept = 'phonely' | 'bolt' | 'officehq' | 'phoneservices';
type Animation = 'base' | 'fade';
type Theme = 'dark' | 'light';
export function App() {
  const [concept, setConcept] = useState<Concept>('phonely');
  const [animation, setAnimation] = useState<Animation>('base');
  const [theme, setTheme] = useState<Theme>('dark');
  const [isExpanded, setIsExpanded] = useState(true);
  // Determine which page to render based on filters
  const currentPage = useMemo(() => {
    // Phonely with animations
    if (concept === 'phonely') {
      if (theme === 'dark') {
        if (animation === 'base') return <LandingPage data-id="element-0" />;
        if (animation === 'fade') return <LandingPageV1 data-id="element-1" />;
      }
      if (theme === 'light') {
        if (animation === 'base') return <LandingPageLight data-id="element-2" />;
        if (animation === 'fade') return <LandingPageV1Light data-id="element-3" />;
      }
    }
    // Bolt with animations
    if (concept === 'bolt') {
      if (theme === 'dark') {
        if (animation === 'base') return <BoltInspiredBase data-id="element-4" />;
        if (animation === 'fade') return <BoltInspired data-id="element-5" />;
      }
      if (theme === 'light') {
        if (animation === 'base') return <BoltInspiredLightBase data-id="element-6" />;
        if (animation === 'fade') return <BoltInspiredLight data-id="element-7" />;
      }
    }
    // OfficeHQ with animations
    if (concept === 'officehq') {
      if (theme === 'dark') {
        if (animation === 'base') return <OfficeHQInspiredBase data-id="element-8" />;
        if (animation === 'fade') return <OfficeHQInspired data-id="element-9" />;
      }
      if (theme === 'light') {
        if (animation === 'base') return <OfficeHQInspiredLightBase data-id="element-10" />;
        if (animation === 'fade') return <OfficeHQInspiredLight data-id="element-11" />;
      }
    }
    // Phone Services with animations
    if (concept === 'phoneservices') {
      if (theme === 'dark') {
        if (animation === 'base') return <PhoneServicesInspiredBase data-id="element-12" />;
        if (animation === 'fade') return <PhoneServicesInspired data-id="element-13" />;
      }
      if (theme === 'light') {
        if (animation === 'base') return <PhoneServicesInspiredLightBase data-id="element-14" />;
        if (animation === 'fade') return <PhoneServicesInspiredLight data-id="element-15" />;
      }
    }
    // Fallback
    return <LandingPage data-id="element-16" />;
  }, [concept, animation, theme]);
  // Animation works with Phonely, Bolt, OfficeHQ, and Phone Services
  const isAnimationDisabled = concept !== 'phonely' && concept !== 'bolt' && concept !== 'officehq' && concept !== 'phoneservices';
  return <div className="relative" data-id="element-17">
      {/* Filter Panel */}
      <div className="fixed top-4 right-4 z-50 bg-[#252525] border border-[#2A2A2A] rounded-2xl shadow-2xl max-w-sm transition-all duration-300" data-id="element-18">
        {/* Header with Toggle */}
        <button onClick={() => setIsExpanded(!isExpanded)} className="w-full flex items-center justify-between p-6 hover:bg-[#2A2A2A]/50 transition-colors rounded-t-2xl" data-id="element-19">
          <h2 className="text-[#F5F5F5] font-['Inter'] text-lg font-bold" data-id="element-20">
            Design Filters
          </h2>
          {isExpanded ? <ChevronUpIcon className="w-5 h-5 text-[#F1B530]" data-id="element-21" /> : <ChevronDownIcon className="w-5 h-5 text-[#F1B530]" data-id="element-22" />}
        </button>

        {/* Collapsible Content */}
        <div className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-23">
          <div className="px-6 pb-6 max-h-[calc(90vh-100px)] overflow-y-auto" data-id="element-24">
            <div className="space-y-6" data-id="element-25">
              {/* Concepts */}
              <div data-id="element-26">
                <label className="text-[#A0A0A0] font-['Inter'] text-xs mb-3 uppercase tracking-wider block" data-id="element-27">
                  1. Concepts
                </label>
                <div className="space-y-2" data-id="element-28">
                  {(['phonely', 'bolt', 'officehq', 'phoneservices'] as Concept[]).map(c => <button key={c} onClick={() => setConcept(c)} className={`w-full px-4 py-2.5 rounded-lg font-['Inter'] text-sm text-left transition-all duration-300 ${concept === c ? 'bg-[#F1B530] text-[#0F0F0F] font-semibold' : 'bg-[#1A1A1A] text-[#A0A0A0] hover:text-[#F5F5F5] hover:bg-[#2A2A2A]'}`} data-id="element-29">
                      {c === 'phonely' ? 'Phonely' : c === 'bolt' ? 'Bolt' : c === 'officehq' ? 'OfficeHQ' : 'Phone Services'}
                    </button>)}
                </div>
              </div>

              {/* Animation */}
              <div data-id="element-30">
                <label className="text-[#A0A0A0] font-['Inter'] text-xs mb-3 uppercase tracking-wider block" data-id="element-31">
                  2. Animation
                </label>
                <div className="space-y-2" data-id="element-32">
                  {(['base', 'fade'] as Animation[]).map(a => <button key={a} onClick={() => !isAnimationDisabled && setAnimation(a)} disabled={isAnimationDisabled} className={`w-full px-4 py-2.5 rounded-lg font-['Inter'] text-sm text-left transition-all duration-300 ${animation === a && !isAnimationDisabled ? 'bg-[#F1B530] text-[#0F0F0F] font-semibold' : isAnimationDisabled ? 'bg-[#1A1A1A] text-[#4A4A4A] cursor-not-allowed' : 'bg-[#1A1A1A] text-[#A0A0A0] hover:text-[#F5F5F5] hover:bg-[#2A2A2A]'}`} data-id="element-33">
                      {a === 'base' ? 'Base (No Animation)' : 'Fade & Rise'}
                    </button>)}
                </div>
                {isAnimationDisabled && <p className="text-[#6B6B6B] font-['Inter'] text-xs mt-2 italic" data-id="element-34">
                    Animation only available for Phonely, Bolt, OfficeHQ, and
                    Phone Services
                  </p>}
              </div>

              {/* Theme */}
              <div data-id="element-35">
                <label className="text-[#A0A0A0] font-['Inter'] text-xs mb-3 uppercase tracking-wider block" data-id="element-36">
                  3. Theme
                </label>
                <div className="grid grid-cols-2 gap-2" data-id="element-37">
                  {(['dark', 'light'] as Theme[]).map(t => <button key={t} onClick={() => setTheme(t)} className={`px-4 py-2.5 rounded-lg font-['Inter'] text-sm transition-all duration-300 ${theme === t ? 'bg-[#F1B530] text-[#0F0F0F] font-semibold' : 'bg-[#1A1A1A] text-[#A0A0A0] hover:text-[#F5F5F5] hover:bg-[#2A2A2A]'}`} data-id="element-38">
                      {t === 'dark' ? 'Dark' : 'Light'}
                    </button>)}
                </div>
              </div>
            </div>

            {/* Current Selection Display */}
            <div className="mt-6 pt-6 border-t border-[#2A2A2A]" data-id="element-39">
              <div className="text-[#A0A0A0] font-['Inter'] text-xs mb-2" data-id="element-40">
                Current Selection:
              </div>
              <div className="text-[#F5F5F5] font-['Inter'] text-sm space-y-1" data-id="element-41">
                <div data-id="element-42">
                  Concept: <span className="text-[#F1B530]" data-id="element-43">{concept}</span>
                </div>
                {(concept === 'phonely' || concept === 'bolt' || concept === 'officehq' || concept === 'phoneservices') && <div data-id="element-44">
                    Animation:{' '}
                    <span className="text-[#F1B530]" data-id="element-45">{animation}</span>
                  </div>}
                <div data-id="element-46">
                  Theme: <span className="text-[#F1B530]" data-id="element-47">{theme}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Render selected page */}
      {currentPage}
    </div>;
}